import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, LoadingRows } from '@shared/ui';

import { isActive } from '../model/job-progress';
import { MODE_LABEL, reportToOpen, rerunTag } from '../model/navigation';

import { Td, Th } from './table';

import type { useBulkJobs } from '../hooks/use-bulk-migrations';

/** Step 0 of every return visit: the module's recent jobs. Opening one resumes it at its step. */
export function JobHistory({
    query,
    onOpen,
    onOpenFinalReport,
}: {
    query: ReturnType<typeof useBulkJobs>;
    onOpen: (reportId: string) => void;
    onOpenFinalReport: (launchReportId: string) => void;
}) {
    return (
        <Card className="mt-6">
            <CardHeader title="Recent jobs" subtitle="The 20 newest for this module" />
            {query.isPending ? (
                <LoadingRows label="Loading history…" />
            ) : query.isError ? (
                <ErrorState error={query.error} onRetry={() => void query.refetch()} />
            ) : query.data.length === 0 ? (
                <EmptyState
                    title="Nothing yet"
                    description="Analyses and migrations show up here."
                />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse">
                        <thead>
                            <tr>
                                <Th>Job</Th>
                                <Th>State</Th>
                                <Th>Clients</Th>
                                <Th>Requested by</Th>
                                <Th>Reason</Th>
                                <Th />
                            </tr>
                        </thead>
                        <tbody>
                            {query.data.map((job) => (
                                <tr
                                    key={job.reportId}
                                    onClick={(event) => {
                                        // A button in the row does its own thing.
                                        if (
                                            event.target instanceof Element &&
                                            event.target.closest('button') !== null
                                        ) {
                                            return;
                                        }
                                        onOpen(reportToOpen(job));
                                    }}
                                    className="cursor-pointer hover:bg-surface-sunken"
                                >
                                    <Td>
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onOpen(reportToOpen(job));
                                            }}
                                            className="font-semibold text-content hover:text-accent-text"
                                        >
                                            {MODE_LABEL[job.mode]}
                                        </button>
                                        {rerunTag(job) !== null && (
                                            <Badge tone="info" className="ml-2">
                                                {rerunTag(job)}
                                            </Badge>
                                        )}
                                        <div className="text-[12px] text-content-muted">
                                            {new Date(job.createdAt).toLocaleString()}
                                        </div>
                                    </Td>
                                    <Td>
                                        <Badge
                                            tone={
                                                job.state === 'failed'
                                                    ? 'danger'
                                                    : isActive(job.state)
                                                      ? 'info'
                                                      : 'neutral'
                                            }
                                        >
                                            {job.state}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        {job.total === null
                                            ? '—'
                                            : `${String(job.done)} / ${String(job.total)}`}
                                    </Td>
                                    <Td className="text-[12.5px]">{job.requestedBy}</Td>
                                    <Td className="max-w-[280px] truncate text-[12.5px] text-content-muted">
                                        {job.reason ?? '—'}
                                    </Td>
                                    <Td>
                                        {/* Only a finished launch has a final report. */}
                                        {job.mode === 'launch' && job.state === 'done' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    onOpenFinalReport(job.reportId);
                                                }}
                                            >
                                                Final report
                                            </Button>
                                        )}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}
